import { DestroyRef, Directive, HostListener, inject, Input, OnInit } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UuidExtraInfoLoggingService } from '@lx/quote-tool/shared/services';
import { getDateStringFromDate } from '@lx/shared/utils';

@Directive({
  standalone: true,
  selector: '[lxStudentDetailsLogging]',
})
export class StudentDetailsLoggingDirective implements OnInit {
  @Input() lxStudentDetailsLogging: string | undefined;

  /**
   * Fields that should be excluded from logging on DOM inner input change events because they are logged via other events such as valueChange or dateChange, ....
   */
  private readonly fieldsExcludedFromLoggingOnInnerInputChange = ['countryOfResidence', 'dateOfBirth'];

  private readonly uuidExtraLoggingService = inject(UuidExtraInfoLoggingService);
  private readonly valueChangeSubject = new Subject<string>();
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Listener for change events (DOM inner events) on the host element.
   */
  @HostListener('change', ['$event.target.value'])
  onChange(value: string) {
    if (this.fieldsExcludedFromLoggingOnInnerInputChange.includes(this.lxStudentDetailsLogging || '')) {
      return; // Skip logging the event triggering by a component that is excluded from logging on inner input change events.
    }
    this.valueChangeSubject.next(value);
  }

  /**
   * Listener for valueChange events (custom valueChange events) on the host element.
   */
  @HostListener('valueChange', ['$event'])
  onValueChange(value: string) {
    this.valueChangeSubject.next(value);
  }

  /**
   * Listener for dateChange events (custom dateChange events) on the host element.
   */
  @HostListener('dateChange', ['$event'])
  onDateChange(value: Date) {
    this.valueChangeSubject.next(getDateStringFromDate(value));
  }

  ngOnInit() {
    this.valueChangeSubject
      .pipe(
        distinctUntilChanged((firstValue, secondValue) => firstValue === secondValue),
        debounceTime(500), // Debounce to avoid logging too many events in a short time
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.uuidExtraLoggingService.logStudentDetails(this.lxStudentDetailsLogging || 'unknown', value);
      });
  }
}
